import { CSSProperties, forwardRef, useEffect, useRef, useState } from "react";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { cn } from "../../lib/utils";

interface TabProps {
  label: string;
  isActive: boolean;
  disabled: boolean;
  onClick: () => void;
}

const Tab = forwardRef<HTMLButtonElement, TabProps>(
  ({ label, isActive, disabled, onClick }, ref) => (
    <button
      ref={ref}
      className={cn(
        "tab-switcher-button flex justify-center items-center border-none bg-transparent p-[7px_16px] relative z-2 transition-colors duration-300 ease text-base font-dm-sans font-bold min-w-[100px] whitespace-nowrap",
        disabled
          ? "disabled text-secondary-bluegrey-3"
          : isActive
            ? "active text-white"
            : "text-[#BCBCBC]"
      )}
      onClick={onClick}
    >
      {label}
    </button>
  )
);

interface TabSwitcherProps {
  tabs: {
    label: string;
    // NOTE: Only static now to deactivate an option by the developpers.
    // Can't be dynamically changed.
    disabled?: boolean;
  }[];
  onTabChange: (tabIndex: number) => void;
  activeTab?: number;
  noSlider?: boolean;
}

export default function TabSwitcher({
  tabs,
  onTabChange,
  activeTab = 0,
  noSlider,
}: TabSwitcherProps) {
  const { isRtl } = useActiveLang();
  const [sliderStyle, setSliderStyle] = useState<CSSProperties>(
    isRtl ? { width: 0, right: 0 } : { width: 0, left: 0 }
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [startingTab] = useState(activeTab);

  useEffect(() => {
    if (containerRef.current) {
      const totalPadding = 6;
      const tabWidthPercentage = 100 / tabs.length;
      const tabWidthWithPadding =
        tabWidthPercentage -
        (totalPadding / containerRef.current.offsetWidth) * 100;
      const tabOffsetWithPadding =
        activeTab * tabWidthPercentage +
        (totalPadding / 2 / containerRef.current.offsetWidth) * 100;

      setSliderStyle((prevStyle) => ({
        ...prevStyle,
        width: `${tabWidthWithPadding}%`,
        left: isRtl ? "unset" : `${tabOffsetWithPadding}%`,
        right: isRtl ? `${tabOffsetWithPadding}%` : "unset",
      }));

      if (activeTab !== startingTab) {
        // Change transition duration when switching tab the first time
        setSliderStyle((prevStyle) => ({
          ...prevStyle,
          transitionDuration: "0.1s",
        }));
      }
    }
  }, [activeTab, tabs.length]);

  const handleTabClick = (index: number) => {
    onTabChange(index);
  };

  return (
    <div
      ref={containerRef}
      className="tab-switcher-container inline-grid grid-flow-col auto-cols-fr relative border border-[#bcbcbc] rounded-[25px] p-[3px] w-max"
    >
      {tabs.map((tab, index) => (
        <Tab
          key={tab.label}
          label={tab.label}
          isActive={index === activeTab}
          disabled={!!tab.disabled}
          onClick={() => {
            if (!tab.disabled) handleTabClick(index);
          }}
        />
      ))}
      {!noSlider && (
        <div
          className="tab-switcher-button active absolute top-[3px] bg-black rounded-2xl transition-[left,right] duration-0 ease h-[calc(100%-6px)]"
          style={sliderStyle}
        />
      )}
    </div>
  );
}
